import React, { useMemo, useState } from "react";
import axios from "axios";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    IconButton,
    Snackbar,
    Alert,
    Grid,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    FormHelperText,
    DialogActions,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";
import {
    Close as CloseIcon,
    AdminPanelSettingsOutlined,
    PersonOutlineOutlined,
    CampaignOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import ApiConfig from "../../Config";

const normalizeRole = (role) => {
    const value = String(role || "")
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, "_");

    if (value === "SUPERADMIN") {
        return "SUPER_ADMIN";
    }

    return value;
};

const AddAdminDialog = ({
    open,
    onClose,
    onStaffAdded,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const accentColor = isDark
        ? "#D1B99A"
        : "#806B52";

    const accentHoverColor = isDark
        ? "#BFA786"
        : "#6D5B45";

    const currentRole = normalizeRole(
        sessionStorage.getItem("role")
    );

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        role: "",
    });

    const [errors, setErrors] = useState({});
    const [openSnackbar, setOpenSnackbar] =
        useState(false);
    const [snackbarMessage, setSnackbarMessage] =
        useState("");
    const [snackbarSeverity, setSnackbarSeverity] =
        useState("success");
    const [loading, setLoading] = useState(false);

    const allRoles = [
        {
            value: "SUPER_ADMIN",
            label: "Super Admin",
            description:
                "Full administrative access",
            icon: <AdminPanelSettingsOutlined />,
        },
        {
            value: "ADMIN",
            label: "Admin",
            description:
                "Manage standard and marketing users",
            icon: <AdminPanelSettingsOutlined />,
        },
        {
            value: "STANDARD",
            label: "Standard",
            description:
                "Standard administrative access",
            icon: <PersonOutlineOutlined />,
        },
        {
            value: "MARKETING",
            label: "Marketing",
            description:
                "Marketing and campaign access",
            icon: <CampaignOutlined />,
        },
    ];

    const roles = useMemo(() => {
        if (currentRole === "SUPER_ADMIN") {
            return allRoles;
        }

        if (currentRole === "ADMIN") {
            return allRoles.filter((role) =>
                ["STANDARD", "MARKETING"].includes(
                    role.value
                )
            );
        }

        return [];
    }, [currentRole]);

    const validate = () => {
        const tempErrors = {
            name: formData.name.trim()
                ? ""
                : "Name is required",

            email: formData.email.trim()
                ? ""
                : "Email is required",

            role: formData.role
                ? ""
                : "Role is required",
        };

        if (
            formData.email.trim() &&
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                formData.email.trim()
            )
        ) {
            tempErrors.email =
                "Enter a valid email address";
        }

        setErrors(tempErrors);

        return Object.values(tempErrors).every(
            (value) => value === ""
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const handleSave = async () => {
        if (!validate()) {
            setSnackbarMessage(
                "Please check the entered details"
            );
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
            return;
        }

        const token =
            sessionStorage.getItem("newToken");
            const adminId =
            sessionStorage.getItem("adminId");

        if (!token) {
            setSnackbarMessage(
                "Session expired. Please login again."
            );
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
            return;
        }

        const dataToSend = {
            name: formData.name.trim(),
            email: formData.email
                .trim()
                .toLowerCase(),
            role: formData.role,
        };

        try {
            setLoading(true);

            const response = await axios.post(
                `${ApiConfig.apiBaseUrl}${ApiConfig.adminRegister}`,
                dataToSend,
                {
                    headers: {
                        "x-access-token": token,
                        "admin-id":adminId
                    },
                }
            );

            if (
                response.status === 200 ||
                response.status === 201
            ) {
                setSnackbarMessage(
                    response.data?.message ||
                        "Staff added successfully!"
                );

                setSnackbarSeverity("success");
                setOpenSnackbar(true);

                handleReset();

                if (onStaffAdded) {
                    await onStaffAdded();
                }
            }
        } catch (error) {
            let message =
                "Failed to add staff";

            if (error.response) {
                switch (
                    error.response.status
                ) {
                    case 400:
                        message =
                            error.response.data
                                ?.message ||
                            "Invalid staff data";
                        break;

                    case 401:
                        message =
                            error.response.data
                                ?.message ||
                            "Unauthorized. Please login again.";
                        break;

                    case 403:
                        message =
                            error.response.data
                                ?.message ||
                            "You do not have permission to create this role.";
                        break;

                    case 409:
                    case 422:
                        message =
                            error.response.data
                                ?.message ||
                            "Email already exists";
                        break;

                    default:
                        message =
                            error.response.data
                                ?.message ||
                            error.response.data
                                ?.error ||
                            "Failed to add staff";
                }
            } else {
                message =
                    "Failed to add staff: Network error";
            }

            setSnackbarMessage(message);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFormData({
            name: "",
            email: "",
            role: "",
        });

        setErrors({});
    };

    const handleDialogClose = () => {
        if (loading) {
            return;
        }

        handleReset();
        onClose();
    };

    const inputSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: 2.5,
            backgroundColor:
                "background.paper",
            transition:
                "all 0.2s ease",

            "&:hover .MuiOutlinedInput-notchedOutline":
                {
                    borderColor:
                        accentColor,
                },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                    borderColor:
                        accentColor,
                    borderWidth: "1px",
                },
        },

        "& .MuiInputLabel-root.Mui-focused": {
            color: accentColor,
        },
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleDialogClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: "hidden",
                        backgroundColor:
                            "background.paper",
                        color:
                            "text.primary",
                        border: "1px solid",
                        borderColor:
                            "divider",
                        boxShadow: isDark
                            ? "0 28px 80px rgba(0,0,0,0.55)"
                            : "0 28px 80px rgba(65,45,25,0.16)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2.5,
                        borderBottom:
                            "1px solid",
                        borderColor:
                            "divider",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "space-between",
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems:
                                    "center",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 2.5,
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    backgroundColor:
                                        isDark
                                            ? "rgba(209,185,154,0.12)"
                                            : "rgba(128,107,82,0.10)",
                                    color:
                                        accentColor,
                                }}
                            >
                                <AdminPanelSettingsOutlined />
                            </Box>

                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{
                                        letterSpacing:
                                            "-0.3px",
                                    }}
                                >
                                    Add New Staff
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 0.4,
                                        color:
                                            "text.secondary",
                                    }}
                                >
                                    Create a new administrator
                                    account
                                </Typography>
                            </Box>
                        </Box>

                        <IconButton
                            aria-label="close"
                            onClick={
                                handleDialogClose
                            }
                            disabled={loading}
                            sx={{
                                width: 38,
                                height: 38,
                                color:
                                    "text.secondary",
                                borderRadius: 2,

                                "&:hover": {
                                    color:
                                        accentColor,
                                    backgroundColor:
                                        isDark
                                            ? "rgba(209,185,154,0.08)"
                                            : "rgba(128,107,82,0.08)",
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        px: 3,
                        py: 3,
                        borderColor:
                            "divider",
                    }}
                >
                    <Grid
                        container
                        spacing={2.2}
                    >
                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                name="name"
                                label="Full Name"
                                placeholder="Enter full name"
                                value={
                                    formData.name
                                }
                                onChange={
                                    handleChange
                                }
                                fullWidth
                                variant="outlined"
                                error={
                                    !!errors.name
                                }
                                helperText={
                                    errors.name
                                }
                                disabled={
                                    loading
                                }
                                sx={
                                    inputSx
                                }
                            />
                        </Grid>

                        <Grid
                            item
                            xs={12}
                        >
                            <TextField
                                name="email"
                                label="Email Address"
                                placeholder="Enter email address"
                                type="email"
                                value={
                                    formData.email
                                }
                                onChange={
                                    handleChange
                                }
                                fullWidth
                                variant="outlined"
                                error={
                                    !!errors.email
                                }
                                helperText={
                                    errors.email
                                }
                                disabled={
                                    loading
                                }
                                sx={
                                    inputSx
                                }
                            />
                        </Grid>

                        <Grid
                            item
                            xs={12}
                        >
                            <FormControl
                                fullWidth
                                error={
                                    !!errors.role
                                }
                                disabled={
                                    loading
                                }
                                sx={
                                    inputSx
                                }
                            >
                                <InputLabel>
                                    Role
                                </InputLabel>

                                <Select
                                    name="role"
                                    value={
                                        formData.role
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    label="Role"
                                    MenuProps={{
                                        PaperProps:
                                            {
                                                sx: {
                                                    mt: 1,
                                                    borderRadius: 2.5,
                                                    border: "1px solid",
                                                    borderColor:
                                                        "divider",
                                                    backgroundColor:
                                                        "background.paper",
                                                    boxShadow:
                                                        isDark
                                                            ? "0 16px 40px rgba(0,0,0,.4)"
                                                            : "0 16px 40px rgba(0,0,0,.12)",
                                                },
                                            },
                                    }}
                                >
                                    {roles.map(
                                        (
                                            role
                                        ) => (
                                            <MenuItem
                                                key={
                                                    role.value
                                                }
                                                value={
                                                    role.value
                                                }
                                                sx={{
                                                    py: 1.2,
                                                    borderRadius: 1.5,
                                                    mx: 0.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        gap: 1.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 34,
                                                            height: 34,
                                                            borderRadius: 1.8,
                                                            display:
                                                                "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",
                                                            backgroundColor:
                                                                isDark
                                                                    ? "rgba(209,185,154,0.10)"
                                                                    : "rgba(128,107,82,0.08)",
                                                            color:
                                                                accentColor,
                                                        }}
                                                    >
                                                        {
                                                            role.icon
                                                        }
                                                    </Box>

                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 13,
                                                                fontWeight: 650,
                                                            }}
                                                        >
                                                            {
                                                                role.label
                                                            }
                                                        </Typography>

                                                        <Typography
                                                            sx={{
                                                                fontSize: 11,
                                                                color:
                                                                    "text.secondary",
                                                                mt: 0.2,
                                                            }}
                                                        >
                                                            {
                                                                role.description
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </MenuItem>
                                        )
                                    )}
                                </Select>

                                {errors.role && (
                                    <FormHelperText>
                                        {
                                            errors.role
                                        }
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.5,
                        borderTop:
                            "1px solid",
                        borderColor:
                            "divider",
                        gap: 1,
                    }}
                >
                    <Button
                        onClick={
                            handleDialogClose
                        }
                        variant="outlined"
                        disabled={loading}
                        sx={{
                            minWidth: 105,
                            height: 42,
                            borderRadius: 2,
                            textTransform:
                                "none",
                            fontWeight: 600,
                            color:
                                "text.primary",
                            borderColor:
                                "divider",
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={
                            handleSave
                        }
                        variant="contained"
                        disabled={loading}
                        startIcon={
                            loading ? (
                                <CircularProgress
                                    size={17}
                                    color="inherit"
                                />
                            ) : (
                                <AdminPanelSettingsOutlined />
                            )
                        }
                        sx={{
                            minWidth: 130,
                            height: 42,
                            borderRadius: 2,
                            textTransform:
                                "none",
                            fontWeight: 700,
                            backgroundColor:
                                accentColor,
                            color: isDark
                                ? "#211F1C"
                                : "#FFFFFF",
                            boxShadow:
                                "none",

                            "&:hover": {
                                backgroundColor:
                                    accentHoverColor,
                                boxShadow:
                                    "none",
                            },
                        }}
                    >
                        {loading
                            ? "Creating..."
                            : "Create User"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={5000}
                onClose={() =>
                    setOpenSnackbar(
                        false
                    )
                }
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={() =>
                        setOpenSnackbar(
                            false
                        )
                    }
                    severity={
                        snackbarSeverity
                    }
                    variant="filled"
                    sx={{
                        width: "100%",
                        borderRadius: 2,
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddAdminDialog;